import { useMediaQuery, useTheme } from "@mui/material";
import { useContext, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getSubPages, parentSubPagesSearch } from "../api/sideNav";
import { SideNavMenuContext } from "../components/sideNav/provider";
import { ActionType, AppDispatch, RootState } from "../reducer";
import { selectAncestorIds } from "../selectors/sideNav";
import { useAppIdMap } from "./app";
import {
  useAddNotebookPagesToState,
  useSelectedNotebookPageIdMap,
} from "./page";
import { useSetting } from "./setting";

export const useInitSideNavOpenListener = () => {
  const { setSideNavOpen } = useSideNavOpen();
  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (smallScreen) {
      setSideNavOpen(false);
    }
  }, [smallScreen]);
};

export const useSideNavOpen = () => {
  const { settingValue, setSettingValue } = useSetting("side-nav-open");
  const sideNavOpen = settingValue === "true";
  const setSideNavOpen = (sideNavOpen: boolean) => {
    setSettingValue(`${sideNavOpen}`);
  };

  return { sideNavOpen, setSideNavOpen };
};

export const useToggleSideNavOpen: () => () => void = () => {
  const { setSideNavOpen, sideNavOpen } = useSideNavOpen();
  const toggleSideNavOpen = () => setSideNavOpen(!sideNavOpen);
  return toggleSideNavOpen;
};

export const useSideNavMenu = () => {
  const ctx = useContext(SideNavMenuContext);
  if (ctx === undefined) {
    throw new Error("Side Nav Context must be used within SideNavMenuProvider");
  }
  return ctx;
};

export const useSubPages = (pageId: string | undefined) => {
  const subPages = useSelector((state: RootState) =>
    pageId ? state.pages.pageSubPagesMap[pageId] : undefined,
  );

  return subPages;
};

export const useFetchSubPages = () => {
  const { appId } = useAppIdMap();
  const addPagesToState = useAddNotebookPagesToState();
  const dispatch = useDispatch();

  const fetchSubPages = (pageId: string) => {
    if (!appId) {
      throw new Error("App Id Must Be Defined");
    }

    return getSubPages({ "page-id": pageId, "app-id": appId, limit: 5 }).then(
      (subPages) => {
        addPagesToState(subPages);
        if (subPages.length === 0) {
          dispatch({
            type: ActionType.ADD_SUB_PAGES,
            pageSubPagesMap: { [pageId]: [] },
          });
        }
      },
    );
  };
  return fetchSubPages;
};

export const useFetchSubPagesListener = (pageId: string) => {
  const { appId } = useAppIdMap();
  const fetchSubPages = useFetchSubPages();
  const subPages = useSubPages(pageId);
  const hasSubPages = subPages !== undefined;

  useEffect(() => {
    if (!pageId) return;
    if (!appId) return;
    if (hasSubPages) return;
    fetchSubPages(pageId);
  }, [pageId, appId, hasSubPages]);
};

export const useFetchParentSubPagesListener = () => {
  const { appId } = useAppIdMap();
  const { selectedNotebookPageId } = useSelectedNotebookPageIdMap();
  const addPages = useAddNotebookPagesToState();
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    if (!appId) return;
    if (!selectedNotebookPageId) return;

    parentSubPagesSearch({
      "page-id": selectedNotebookPageId,
      "app-id": appId,
    }).then((res) => {
      const pages = Object.values(res).flat();
      addPages(pages);
      dispatch({ type: ActionType.ADD_SUB_PAGES, pageSubPagesMap: res });
    });
  }, [selectedNotebookPageId, appId]);
};

export const useIsAncestorOfSelected = (pageId: string): boolean => {
  const ancestorIds = useSelector(selectAncestorIds);
  return ancestorIds.has(pageId);
};
